import { ALL_SPORTS } from '../constants'
import type { ProgressSport } from '../types'

const GENERIC_KEY_SEPARATOR = ':'

/**
 * A key unique per `/progress/sports` entry. Specific generic sports share the
 * base Generic `id`, so their key appends `generic_sport.id`; regular sports
 * and plain Generic use their `id`.
 */
export const getSportKey = (sport: ProgressSport): string =>
  sport.generic_sport
    ? `${sport.id}${GENERIC_KEY_SEPARATOR}${sport.generic_sport.id}`
    : sport.id

/** The entry for `key`; undefined for null, `ALL_SPORTS` or an unknown key. */
export const findSportByKey = (
  sports: ProgressSport[],
  key: string | null
): ProgressSport | undefined =>
  key === null ? undefined : sports.find((s) => getSportKey(s) === key)

/** The chip label: the generic sport's name, else the sport's full name. */
export const getSportLabel = (sport: ProgressSport): string =>
  sport.generic_sport?.name ?? sport.name_full

export interface SportQuery {
  /** The selected entry; undefined for All Sports or no selection. */
  sport: ProgressSport | undefined
  /** The entry's `id`, `ALL_SPORTS`, or null when nothing is selected. */
  sportId: string | null
  /** The entry's `generic_sport.id`; undefined unless a specific generic sport. */
  genericSportId: string | undefined
}

/** The ids every stats query is keyed and filtered by, from a selection key. */
export const resolveSportQuery = (
  sports: ProgressSport[],
  key: string | null
): SportQuery => {
  if (key === ALL_SPORTS) {
    return { sport: undefined, sportId: ALL_SPORTS, genericSportId: undefined }
  }
  const sport = findSportByKey(sports, key)
  return {
    sport,
    sportId: sport?.id ?? null,
    genericSportId: sport?.generic_sport?.id,
  }
}
